import { DocumentGroup, DocumentType, GroupedDocument, OwnedDocument } from '@pelicargo/types';

import { getMissingDocumentTypes } from '../../helpers';

export const VIEWER_GROUP_TITLE = 'Your Documents';

const VIEWER_KEY = 'viewer';
const UNOWNED_KEY = 'unowned';
const PENDING_KEY = 'pending';
const PENDING_TITLE = 'Awaiting Upload';
const MAWB_LABEL = 'MAWB';
const HAWB_LABEL_PREFIX = 'HAWB';

type Options = {
  documents: OwnedDocument[];
  /** The account the viewer is acting as. Its group is titled "Your Documents" and sorts first. */
  viewerAccountId?: number;
  /** The account the request belongs to — the forwarder, who owes the expected documents. */
  requestAccount?: { id?: number; name?: string | null };
  /**
   * Title for documents with no account. Uploads require one now, so this is a rearguard for
   * rows that predate that and for the paths still handing over a nullable account (the
   * provider quote import passes the request's). A document nobody can attribute has to be
   * shown somewhere rather than dropped.
   */
  tenantTitle?: string;
  /** Types the shipment expects. Each one nobody has uploaded yet gets a placeholder row. */
  expectedTypes?: DocumentType[];
  /**
   * MAWB / HAWB tags keyed by document id, resolved from the shipment form each airway bill
   * is attached to. Airway bills are indistinguishable without them — see below.
   */
  labelByDocumentId?: Record<number, string>;
};

/**
 * Every document on a request belongs to everyone on it — the customer, the fulfillment
 * partner and the tenant all see the same set — so document lists never filter by owner
 * or by an expected-type whitelist, they only group by owner. Filtering by type used to
 * drop whatever the viewer wasn't expected to provide (an FP's LABELS, a BOL on a
 * non-FCA shipment, a second copy of a type), leaving it invisible and undownloadable.
 *
 * A type the shipment expects but nobody has uploaded becomes a placeholder row, filed
 * under the forwarder who owes it — not under whoever happens to be looking, which on the
 * marketplace read as the FP's own paperwork. Without a known forwarder the placeholders
 * get their own group rather than being attributed to an account that didn't ask for them.
 */
export const groupByAccount = ({
  documents,
  viewerAccountId,
  requestAccount,
  tenantTitle = 'Shared',
  expectedTypes = [],
  labelByDocumentId,
}: Options): DocumentGroup[] => {
  const uploaded = (documents ?? []).filter((doc) => doc?.id != null);
  const viewerKey = viewerAccountId != null ? String(viewerAccountId) : VIEWER_KEY;

  const groups = new Map<string, DocumentGroup>();

  const isViewers = (accountId?: number | null) => accountId != null && accountId === viewerAccountId;

  const keyFor = (accountId?: number | null) => {
    if (isViewers(accountId)) return String(accountId);
    return accountId == null ? UNOWNED_KEY : String(accountId);
  };

  const titleFor = (accountId?: number, accountName?: string | null) => {
    if (isViewers(accountId)) return VIEWER_GROUP_TITLE;
    return accountName || (accountId == null ? tenantTitle : `Account ${accountId}`);
  };

  const groupFor = (key: string, title: string) => {
    if (!groups.has(key)) groups.set(key, { key, title, documents: [] });
    return groups.get(key);
  };

  const labelFor = (doc: OwnedDocument) => (doc.id != null ? labelByDocumentId?.[doc.id] : undefined);

  uploaded.forEach((doc) => {
    groupFor(keyFor(doc.account_id), titleFor(doc.account_id, doc.account?.name)).documents.push({
      ...doc,
      label: labelFor(doc),
    });
  });

  // House bills are stored as AIRWAY_BILL too, so a HAWB must not satisfy the airway bill the
  // shipment expects — that one is always the master. The shipment-form labels are the only
  // semi-reliable way to tell the two apart (filenames aren't and without adding schema changes), so an unlabelled airway bill counts
  // as the MAWB and callers with no labels to give keep the plain any-AWB behaviour.
  const isHawb = (doc: OwnedDocument) => !!labelFor(doc)?.startsWith(HAWB_LABEL_PREFIX);
  const missingTypes = getMissingDocumentTypes(
    uploaded.filter((doc) => !isHawb(doc)),
    expectedTypes,
  );
  if (missingTypes.length) {
    const group =
      requestAccount?.id != null
        ? groupFor(keyFor(requestAccount.id), titleFor(requestAccount.id, requestAccount.name))
        : groupFor(PENDING_KEY, PENDING_TITLE);

    group.documents.push(
      ...missingTypes.map((type) => ({
        type,
        label: type === DocumentType.AIRWAY_BILL ? MAWB_LABEL : undefined,
      })),
    );
  }

  // Within a group, keep the order the expected types were listed in (that ordering is
  // what the paperwork UI reads top to bottom), then upload order. Placeholders have no
  // id, so they fall in behind any real document of the same type.
  const typeRank = (doc: OwnedDocument) => {
    const index = expectedTypes.indexOf(doc.type);
    return index === -1 ? expectedTypes.length : index;
  };
  // Airway bills all share a type, so id order would scatter the house bills by whenever they
  // happened to be uploaded. Read them the way they're numbered: master first, then HAWB 1..n.
  const labelRank = (doc: GroupedDocument) =>
    doc.label === MAWB_LABEL ? 0 : Number(doc.label?.slice(HAWB_LABEL_PREFIX.length)) || 0;
  const byReadingOrder = (a: GroupedDocument, b: GroupedDocument) =>
    typeRank(a) - typeRank(b) ||
    (a.label && b.label ? labelRank(a) - labelRank(b) : 0) ||
    (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER);

  const rank = (key: string) => {
    if (key === viewerKey) return 0;
    if (key === UNOWNED_KEY) return 2;
    if (key === PENDING_KEY) return 3;
    return 1;
  };

  // `sort` mutates its receiver, so both calls here are given an array built in this function: the
  // caller's `documents` is never reordered, and the groups are produced by transformation rather
  // than by sorting the map's own arrays in place.
  return [...groups.values()]
    .map((group) => ({ ...group, documents: [...group.documents].sort(byReadingOrder) }))
    .sort((a, b) => rank(a.key) - rank(b.key) || a.title.localeCompare(b.title));
};
